import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";

interface NotFoundProps {
  /** Heading shown to the user. */
  title?: string;
  /** Explanatory line under the heading. */
  message?: string;
  /** Seconds before auto-redirecting home. Pass 0 to disable the countdown. */
  redirectSeconds?: number;
}

/**
 * Friendly "content not available" page. Used both for unknown routes (404) and
 * for authenticated users who reach a tab they're not permitted to view. Tells the
 * user what happened, then returns them home — automatically after a short countdown
 * or immediately via the button.
 */
export default function NotFound({
  title = "Content not available",
  message = "The page you’re looking for doesn’t exist, or you don’t have access to it.",
  redirectSeconds = 8,
}: NotFoundProps) {
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(redirectSeconds);

  useEffect(() => {
    if (redirectSeconds <= 0) return;
    if (seconds <= 0) {
      navigate("/", { replace: true });
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, redirectSeconds, navigate]);

  return (
    <div
      className="flex w-full items-center justify-center bg-[var(--bg-canvas)]"
      style={{ minHeight: "100vh" }}
      role="alert"
      aria-live="polite"
    >
      <div className="flex max-w-md flex-col items-center rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-10 py-12 text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--bg-accent-subtle)]">
          <i className="pi pi-compass text-3xl text-[var(--text-primary)]" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-[var(--text-primary)]">{title}</h1>
        <p className="mb-1 max-w-sm text-sm leading-relaxed text-[var(--text-secondary)]">{message}</p>
        {redirectSeconds > 0 && (
          <p className="mb-6 text-xs text-[var(--text-disabled)]">Redirecting to home in {seconds}s…</p>
        )}
        {redirectSeconds <= 0 && <div className="mb-6" />}
        <Button
          label="Go to Home"
          icon="pi pi-home"
          onClick={() => navigate("/", { replace: true })}
          style={{ background: "transparent", borderColor: "var(--btn-primary-border)", color: "var(--btn-primary-bg)" }}
        />
      </div>
    </div>
  );
}
